<template>
  <v-container fluid>
    <v-row no-gutters>
      <v-col cols="12">
        <v-card elevation="4" :loading="loading">
          <v-card-title class="text-indigo text-button">
            {{ editing ? "Modificar mapeador" : "Crear mapeador" }}
          </v-card-title>

          <v-form ref="formRef" @submit.prevent="save">
            <v-container fluid>
              <v-row>
                <v-col cols="12">
                  <h4 class="text-button text-primary">Identificación</h4>
                  <v-divider />
                  <v-container fluid>
                    <v-row>
                      <v-col v-if="editing" cols="12" sm="6" lg="4">
                        <v-skeleton-loader v-if="loading" type="list-item-two-line" />
                        <v-text-field
                          v-else
                          :model-value="data.id"
                          label="ID"
                          prepend-inner-icon="mdi-identifier"
                          readonly
                          hint="Identificador interno, asignado por el sistema."
                          persistent-hint
                        />
                      </v-col>
                      <v-col cols="12" sm="6" lg="4">
                        <v-skeleton-loader v-if="loading" type="list-item-two-line" />
                        <v-text-field
                          v-else
                          v-model="data.name"
                          label="Nombre"
                          prepend-inner-icon="mdi-rename-box"
                          :disabled="saving"
                          :rules="[rules.required]"
                          :error-messages="errors.for('name')"
                          @update:model-value="errors.clear('name')"
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                </v-col>

                <v-col cols="12">
                  <h4 class="text-button text-primary">Correspondencias</h4>
                  <v-divider />
                  <v-container fluid>
                    <p class="text-body-2 text-medium-emphasis mb-4">
                      Cada fila asocia un campo del usuario con el RDN del certificado
                      del que se toma su valor al importar un p12.
                    </p>

                    <v-skeleton-loader v-if="loading" type="list-item-three-line" />
                    <template v-else>
                      <v-row v-for="(entry, idx) in entries" :key="idx" align="center">
                        <v-col cols="12" sm="5">
                          <v-select
                            v-model="entry.key"
                            :items="fieldItems"
                            :item-props="true"
                            label="Dato del usuario"
                            :disabled="saving"
                          />
                        </v-col>
                        <v-col cols="12" sm="5">
                          <v-text-field
                            v-model="entry.value"
                            label="Dato en certificado"
                            placeholder="CN, SERIALNUMBER, E, GIVENNAME…"
                            :disabled="saving"
                          />
                        </v-col>
                        <v-col cols="12" sm="2">
                          <v-btn
                            icon="mdi-delete"
                            variant="text"
                            color="error"
                            :disabled="saving"
                            @click="entries.splice(idx, 1)"
                          />
                        </v-col>
                      </v-row>

                      <v-btn
                        v-if="entries.length < USER_MAPPER_FIELDS.length"
                        variant="text"
                        color="indigo"
                        :disabled="saving"
                        @click="addEntry"
                      >
                        <v-icon class="mr-1">mdi-plus</v-icon>Agregar correspondencia
                      </v-btn>
                    </template>
                  </v-container>
                </v-col>
              </v-row>
            </v-container>

            <v-card-actions>
              <v-spacer />
              <v-btn
                type="submit"
                variant="text"
                color="success"
                class="mx-1"
                :loading="saving"
                :disabled="loading"
              >
                <v-icon class="mr-1">mdi-content-save</v-icon>Guardar
              </v-btn>
              <v-btn
                variant="text"
                color="error"
                class="mx-1"
                :disabled="saving"
                @click="router.push('/admin/mappers')"
              >
                Cancelar
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import type { MapperCreatePayload, UserMapperField } from "bancojs";
import { banco } from "@/api/client";
import { useApiErrors } from "@/composables/useApiErrors";
import { useFormErrors } from "@/composables/useFormErrors";
import { useNotificationsStore } from "@/stores/notifications";

const route = useRoute();
const router = useRouter();
const { report } = useApiErrors();
const notifications = useNotificationsStore();
const errors = useFormErrors();

const formRef = ref<{ validate: () => Promise<{ valid: boolean }> } | null>(null);
const editing = computed(() => !!route.params.id);
const mapperId = computed(() => Number(route.params.id));

/**
 * Las únicas claves que CertificateService reconoce.
 *
 * El servicio compara con equalsIgnoreCase contra este conjunto fijo; cualquier
 * otra clave se ignora en silencio, así que ofrecer texto libre acá sería
 * invitar a configurar algo que nunca se aplica.
 */
const USER_MAPPER_FIELDS: ReadonlyArray<{ field: UserMapperField; label: string }> = [
  { field: "username", label: "Nombre de usuario" },
  { field: "name", label: "Nombre" },
  { field: "email", label: "Email" },
  { field: "firstname", label: "Nombres" },
  { field: "lastname", label: "Apellidos" },
  { field: "dni", label: "RUT" },
];

const loading = ref(true);
const saving = ref(false);
const data = ref({ id: undefined as number | undefined, name: "" });
const entries = ref<Array<{ key: UserMapperField; value: string }>>([]);

const rules = { required: (val: unknown) => !!val || "Campo requerido." };

/** Una clave ya usada no se puede volver a elegir. */
const fieldItems = computed(() =>
  USER_MAPPER_FIELDS.map((f) => ({
    title: f.label,
    value: f.field,
    props: { disabled: entries.value.some((e) => e.key === f.field) },
  }))
);

function addEntry() {
  const free = USER_MAPPER_FIELDS.find(
    (f) => !entries.value.some((e) => e.key === f.field)
  );
  if (free) entries.value.push({ key: free.field, value: "" });
}

async function load() {
  if (!editing.value) {
    addEntry();
    loading.value = false;
    return;
  }
  try {
    const mapper = await banco.admin.mappers.get(mapperId.value);
    data.value.id = mapper.id;
    data.value.name = mapper.name;
    entries.value = Object.entries(mapper.userMapper ?? {}).map(([key, value]) => ({
      key: key as UserMapperField,
      value: String(value ?? ""),
    }));
    if (entries.value.length === 0) addEntry();
  } catch (err) {
    report(err, {
      title: "Mapeador",
      fallback: "Ocurrió un error al recuperar el mapeador.",
    });
  } finally {
    loading.value = false;
  }
}

async function save() {
  const validation = await formRef.value?.validate();
  if (validation && !validation.valid) return;

  errors.reset();
  saving.value = true;

  const payload: MapperCreatePayload = {
    name: data.value.name,
    // `userMapper` en camelCase es la ÚNICA excepción de toda la API, que va en
    // snake_case: por eso el serializador de bancojs no transforma la caja.
    userMapper: entries.value
      .filter((e) => e.key && e.value)
      .reduce<Record<string, string>>((acc, e) => ({ ...acc, [e.key]: e.value }), {}),
  };

  try {
    if (editing.value) {
      // PUT y no PATCH: el mapa de correspondencias se reemplaza completo, así
      // que quitar una fila tiene que borrarla en el servidor.
      await banco.admin.mappers.update(mapperId.value, payload);
      notifications.success("Se guardaron los cambios en el mapeador.", "Guardado");
    } else {
      await banco.admin.mappers.create(payload);
      notifications.success("Se creó el mapeador exitosamente.", "Creado");
    }
    router.push("/admin/mappers");
  } catch (err) {
    report(err, {
      title: "Mapeador",
      fallback: "Ocurrió un error al guardar el mapeador.",
      form: errors,
    });
  } finally {
    saving.value = false;
  }
}

onMounted(load);
</script>
